import {
  OAUTH_COMPLETION_URL_PARAM,
  RETURN_TO_PARAM,
  buildAuthCompletePath,
  readAuthHandoffUrl,
  sanitizeOAuthCompletionUrl,
  sanitizeReturnTo,
} from './returnTo';
import { paths } from './routes';

export const SESSION_TASK_COMPLETION_STORAGE_KEY = 'mcv:clerk-session-task-completion';

export const SESSION_TASK_PATHS = {
  'choose-organization': paths.sessionTasks.chooseOrganization,
  'reset-password': paths.sessionTasks.resetPassword,
  'setup-mfa': paths.sessionTasks.setupMfa,
} as const;

export type SessionTaskKey = keyof typeof SESSION_TASK_PATHS;

export const getSessionTaskPath = (key: string | null | undefined): string | null => {
  if (!key || !(key in SESSION_TASK_PATHS)) return null;
  return SESSION_TASK_PATHS[key as SessionTaskKey];
};

const getSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Preserve the destination of an authentication flow while Clerk redirects a
 * pending session through one or more task pages. Session storage is scoped to
 * the current tab and origin, which keeps parallel sign-ins isolated while
 * surviving the full-page redirects used by OAuth and satellite hand-offs.
 */
export const rememberSessionTaskCompletionTarget = (
  rawTarget: string,
  origin = window.location.origin
): string | null => {
  const target = sanitizeOAuthCompletionUrl(rawTarget, origin);
  if (!target) return null;

  try {
    getSessionStorage()?.setItem(SESSION_TASK_COMPLETION_STORAGE_KEY, target);
  } catch {
    // Storage can be unavailable under strict privacy policies. The task page
    // retains a safe /auth/complete fallback in that case.
  }

  return target;
};

export const readSessionTaskCompletionTarget = (origin = window.location.origin): string | null => {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const rawTarget = storage.getItem(SESSION_TASK_COMPLETION_STORAGE_KEY);
    const target = sanitizeOAuthCompletionUrl(rawTarget, origin);
    if (!target && rawTarget) {
      storage.removeItem(SESSION_TASK_COMPLETION_STORAGE_KEY);
    }
    return target;
  } catch {
    return null;
  }
};

type LocationParts = {
  pathname: string;
  search: string;
  hash: string;
};

const AUTH_PLUMBING_PATHS = [
  paths.publicAuth.authComplete,
  paths.publicAuth.signIn,
  paths.publicAuth.signInLegacy,
  paths.publicAuth.signUp,
  paths.publicAuth.signUpProvider,
  paths.publicAuth.signUpEmployer,
  paths.publicAuth.loginOrCreateAccount,
  paths.publicAuth.forgotPassword,
  paths.publicAuth.ssoCallback,
  '/signUp',
  ...Object.values(SESSION_TASK_PATHS),
];

const isAuthPlumbingPath = (pathname: string): boolean =>
  AUTH_PLUMBING_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * Chooses the destination to resume after a pending session finishes its tasks.
 * Explicit auth hand-offs win over the plumbing route that carries them; an
 * ordinary application URL wins over stale storage; auth-only routes fall back
 * to the target recorded when their flow started.
 */
export const resolveSessionTaskCompletionTarget = (
  location: LocationParts,
  origin = window.location.origin
): string => {
  const searchParams = new URLSearchParams(location.search);
  const carriedTarget =
    searchParams.get(OAUTH_COMPLETION_URL_PARAM) ??
    readAuthHandoffUrl(location.search, location.hash);
  const sanitizedCarriedTarget = sanitizeOAuthCompletionUrl(carriedTarget, origin);
  if (sanitizedCarriedTarget) return sanitizedCarriedTarget;

  // `return_to` is an inner application destination, not itself a safe task
  // completion URL. Validate it with the narrower app-path sanitizer, then
  // rebuild the auth-complete carrier. Rebuilding also avoids persisting Clerk
  // handshake parameters that may be present beside it in the current URL.
  const carriedReturnTo = sanitizeReturnTo(searchParams.get(RETURN_TO_PARAM), origin);
  if (carriedReturnTo) return buildAuthCompletePath(carriedReturnTo);

  if (!isAuthPlumbingPath(location.pathname)) {
    const currentTarget = sanitizeOAuthCompletionUrl(
      `${location.pathname}${location.search}${location.hash}`,
      origin
    );
    if (currentTarget) return currentTarget;
  }

  return readSessionTaskCompletionTarget(origin) ?? paths.publicAuth.authComplete;
};
